import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { KPI } from '../api';
import {
  ENERGY_SETTINGS_MENU_STORAGE_KEY,
  DECISION_SETTINGS_MENU_STORAGE_KEY,
  ENERGY_COMPARISON_SETTINGS_MENU_STORAGE_KEY,
  SCENARII_INVEST_PLAN_SETTINGS_MENU_STORAGE_KEY
} from './constants';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { first } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';


export interface ObservableMap<T> {
  [key: string]: Observable<T>;
}

@Injectable({
  providedIn: 'root',
})
export class SettingsMenusService {

  constructor(
    private translate: TranslateService
  ) {
    const label = this.translate.instant('Investment history');
    const currentYear = new Date().getFullYear()
    this.DEFAULT_MENUS.DECISION.invest_history_N.label = label + ' ' + currentYear;
    this.DEFAULT_MENUS.DECISION.invest_history_N1.label = label + ' ' + (currentYear - 1);
  }

  private readonly MENU_STORAGE_KEYS: { [key in KPI.ElementsChoiceMenuType]: string } = {
    ENERGY: ENERGY_SETTINGS_MENU_STORAGE_KEY,
    ENERGY_COMPARISON: ENERGY_COMPARISON_SETTINGS_MENU_STORAGE_KEY,
    DECISION: DECISION_SETTINGS_MENU_STORAGE_KEY,
    SCENARII_INVEST_PLAN: SCENARII_INVEST_PLAN_SETTINGS_MENU_STORAGE_KEY,
  };

  private readonly DEFAULT_MENUS: { [key in KPI.ElementsChoiceMenuType]: KPI.ElementsChoiceMenu } = {
    ENERGY: {
      energy_carbon_trajectory: {
        id: 'energy_carbon_trajectory',
        label: _('kWh trajectory'),
        value: true,
        requireEnergy: true
      },
      stranded_sites: {
        id: 'stranded_sites',
        label: _('Drop-outs vs. target'),
        value: false,
        requireMultipleSites: true,
      },
      /*avoided_ghg_emissions: {
        id: 'avoided_ghg_emissions',
        label: _('cumulated_avoided_ghg_emissions'),
        value: false,
      },*/
    },
    ENERGY_COMPARISON: {
      energy_trajectory: {
        id: 'energy_trajectory',
        label: _('kWh trajectory'),
        value: true,
        requireEnergy: true
      },
      carbon_trajectory: {
        id: 'carbon_trajectory',
        label: _('Carbon trajectory'),
        value: false,
        requireEnergy: true
      },
    },
    DECISION: {
      pluriannual_plan: {
        id: 'pluriannual_plan',
        label: _('Plurianual plan'),
        value: true,
        requireEnergy: false
      },
      validated_vs_engaged: {
        id: 'validated_vs_engaged',
        label: _('Paid vs engaged'),
        value: false,
        requireEnergy: false,
        requireAriba: true,
      },
      invest_history_N: {
        id: 'invest_history_N',
        label: '',
        value: false,
        requireEnergy: false
      },
      invest_history_N1: {
        id: 'invest_history_N1',
        label: '',
        value: false,
        requireEnergy: false
      },
      technicals_lots: {
        id: 'technicals_lots',
        label: _('Technicals lots'),
        value: false,
        requireEnergy: false
      },
    },
    SCENARII_INVEST_PLAN: {
      impact_CAPEX: {
        id: 'impact_CAPEX',
        label: _('Energy / carbon / RSE impact CAPEX'),
        value: true,
      },
    }
  };

  private _elementsChoiceMenu$: {
    [key in KPI.ElementsChoiceMenuType]: BehaviorSubject<KPI.ElementsChoiceMenu>
  } = {
      ENERGY: new BehaviorSubject<KPI.ElementsChoiceMenu>(null),
      ENERGY_COMPARISON: new BehaviorSubject<KPI.ElementsChoiceMenu>(null),
      DECISION: new BehaviorSubject<KPI.ElementsChoiceMenu>(null),
      SCENARII_INVEST_PLAN: new BehaviorSubject<KPI.ElementsChoiceMenu>(null),
    };

  private cachedMenuLoaded: { [key in KPI.ElementsChoiceMenuType]: boolean } = {
    ENERGY: false,
    ENERGY_COMPARISON: false,
    DECISION: false,
    SCENARII_INVEST_PLAN: false,
  };

  public elementsChoiceMenu(type: KPI.ElementsChoiceMenuType): Observable<KPI.ElementsChoiceMenu> {
    if (!this.cachedMenuLoaded[type]) {
      const cachedMenu = localStorage.getItem(this.MENU_STORAGE_KEYS[type]);
      const cachedMenuParsed = JSON.parse(cachedMenu);
      if (cachedMenuParsed && Object.keys(cachedMenuParsed).length === Object.keys(this.DEFAULT_MENUS[type]).length) {
        const mergedMenu = Object.keys(this.DEFAULT_MENUS[type]).reduce((acc, key) => {
          acc[key] = {
            ...this.DEFAULT_MENUS[type][key],
            value: cachedMenuParsed[key]?.value ?? this.DEFAULT_MENUS[type][key].value
          };
          return acc;
        }, {} as KPI.ElementsChoiceMenu);
        this._elementsChoiceMenu$[type].next(mergedMenu);
      } else {
        this._elementsChoiceMenu$[type].next(this.DEFAULT_MENUS[type]);
      }
      this.cachedMenuLoaded[type] = true;
    }
    return this._elementsChoiceMenu$[type];
  }

  public setElementsChoiceMenu(type: KPI.ElementsChoiceMenuType, menu: KPI.ElementsChoiceMenu): void {
    this._elementsChoiceMenu$[type].next(menu);
    localStorage.setItem(this.MENU_STORAGE_KEYS[type], JSON.stringify(menu));
  }

  public toggleMenuElement(type: KPI.ElementsChoiceMenuType, itemKey: string, toggled: boolean): void {
    this.elementsChoiceMenu(type).pipe(first()).subscribe((menu) => {
      const newMenu = {
        ...menu,
        [itemKey]: {
          ...menu[itemKey],
          value: toggled,
        },
      };
      this.setElementsChoiceMenu(type, newMenu);
    });
  }
}
